/**
 * search.js — TFD Search panel + predictive quicksearch
 * Matches production tf.design behavior: inline panel below header,
 * horizontal product card results, close-on-escape.
 */
(function () {
  'use strict';

  var header = document.querySelector('.main-header');
  var toggle = document.querySelector('[data-search-toggle]');
  var panel = document.querySelector('.search-panel');
  if (!header || !toggle || !panel) return;

  var form = panel.querySelector('.quicksearch-form');
  var input = panel.querySelector('input[name="q"]');
  var closeBtn = panel.querySelector('.close');
  var results = panel.querySelector('.quicksearch-results');
  var curtain = document.querySelector('.curtain');

  var debounceTimer = null;

  /* ---------- Open / Close ---------- */

  function open() {
    header.classList.add('search-open');
    if (curtain) curtain.classList.add('is-visible');
    setTimeout(function () { if (input) input.focus(); }, 100);
  }

  function close() {
    header.classList.remove('search-open');
    if (curtain) curtain.classList.remove('is-visible');
    if (input) input.value = '';
    clearResults();
    toggle.focus();
  }

  function clearResults() {
    if (results) {
      results.innerHTML = '';
      results.classList.remove('has-results');
    }
    if (form) form.classList.remove('has-results');
  }

  toggle.addEventListener('click', function (e) {
    e.preventDefault();
    if (header.classList.contains('search-open')) {
      close();
    } else {
      open();
    }
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  }

  /* Close on curtain click */
  if (curtain) {
    curtain.addEventListener('click', function () {
      if (header.classList.contains('search-open')) close();
    });
  }

  /* Close on Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && header.classList.contains('search-open')) {
      close();
    }
  });

  /* ---------- Predictive Search ---------- */

  if (!results || !input) return;

  /* Storefront API token (public-safe; scoped to unauthenticated_read_product_listings).
     Used for SKU-prefix fallback search since /search/suggest.json doesn't index variant SKUs. */
  var SF_TOKEN = '7871ea81528b198c571913df19564914';
  var SF_API = '/api/2024-10/graphql.json';

  function fetchPredictive(query) {
    if (!query || query.length < 2) {
      clearResults();
      return;
    }

    var suggestUrl = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product,article,page&resources[limit]=6';

    Promise.all([
      fetch(suggestUrl).then(function (r) { return r.json(); }).catch(function () { return null; }),
      fetchSkuMatches(query)
    ]).then(function (results) {
      renderResults(query, results[0], results[1]);
    });
  }

  /* Query the Storefront GraphQL search endpoint.
     Shopify's storefront `search` matches on title/tag/vendor AND variant SKU,
     so a SKU prefix like "V-02" surfaces the right product.

     IMPORTANT: Shopify's search does NOT prefix-match a trailing hyphen+digit
     fragment. e.g. raw query "D-0" returns ZERO nodes even though SKUs
     D-01..D-09 exist (yet "D-" and "D-01" both return results). Appending an
     explicit trailing wildcard ("D-0*") makes it prefix-match reliably for
     every keystroke length. The client-side SKU startsWith filter in
     renderResults() still enforces precision against the RAW query, and normal
     word queries ("mod" -> "mod*") are unaffected. */
  function fetchSkuMatches(query) {
    var wildcardQuery = query.replace(/\*+$/, '') + '*';
    return fetch(SF_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF_TOKEN
      },
      body: JSON.stringify({
        query: 'query($q:String!){ search(first:10, query:$q, types:PRODUCT){ nodes{ ... on Product{ handle title onlineStoreUrl featuredImage{ url } priceRange{ minVariantPrice{ amount } } variants(first:10){ nodes{ sku } } } } } }',
        variables: { q: wildcardQuery }
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || !j.data || !j.data.search) return [];
        return j.data.search.nodes;
      })
      .catch(function () { return []; });
  }

  function renderResults(query, suggestData, gqlNodes) {
    var resources = suggestData && suggestData.resources && suggestData.resources.results;
    var suggestProducts = (resources && resources.products) || [];

    /* Build set of handles from /products/<handle> URLs in predictive results */
    var seenHandles = {};
    suggestProducts.forEach(function (p) {
      var m = (p.url || '').match(/\/products\/([^/?#]+)/);
      if (m) seenHandles[m[1]] = true;
    });

    /* SKU-only matches: products from GraphQL that aren't already in suggest results,
       AND whose variant SKU actually starts with the query (case-insensitive).
       This keeps the dropdown noise-free for normal word queries. */
    var qUpper = query.toUpperCase();
    var skuOnly = (gqlNodes || []).filter(function (n) {
      if (!n || !n.handle || seenHandles[n.handle]) return false;
      var variants = (n.variants && n.variants.nodes) || [];
      return variants.some(function (v) {
        return v.sku && v.sku.toUpperCase().indexOf(qUpper) === 0;
      });
    });

    var html = '';
    var hasItems = false;

    if (suggestProducts.length) {
      hasItems = true;
      html += '<div class="result-list">';
      suggestProducts.forEach(function (item) {
        var img = item.image
          ? '<div class="picture"><img src="' + item.image + '" alt="" loading="lazy"></div>'
          : '<div class="picture"></div>';
        html += '<a href="' + item.url + '" class="result-item">' +
          img +
          '<h2><span>' + escapeHtml(item.title) + '</span>' +
          '<span>' + formatMoney(item.price) + '</span></h2>' +
        '</a>';
      });
      html += '</div>';
    }

    if (skuOnly.length) {
      hasItems = true;
      html += '<div class="result-list result-list--sku">';
      skuOnly.forEach(function (n) {
        var url = n.onlineStoreUrl || ('/products/' + n.handle);
        var imgUrl = n.featuredImage && n.featuredImage.url;
        var img = imgUrl
          ? '<div class="picture"><img src="' + imgUrl + '" alt="" loading="lazy"></div>'
          : '<div class="picture"></div>';
        var price = n.priceRange && n.priceRange.minVariantPrice && n.priceRange.minVariantPrice.amount;
        html += '<a href="' + url + '" class="result-item">' +
          img +
          '<h2><span>' + escapeHtml(n.title) + '</span>' +
          '<span>' + (price ? formatMoney(price) : '') + '</span></h2>' +
        '</a>';
      });
      html += '</div>';
    }

    if (hasItems) {
      html += '<footer><a href="' + searchPageUrl(query) +
        '" class="all">View all results</a></footer>';
      results.innerHTML = html;
      results.classList.add('has-results');
      form.classList.add('has-results');
    } else {
      clearResults();
    }
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Shopify storefront search does not prefix-match a trailing hyphen+digit
     fragment: /search?q=D-0 returns "No results" even though D-01..D-09 exist
     (same root cause fixed in the predictive dropdown). Append a trailing
     wildcard so the full results page matches the same way the dropdown does.
     search-results.liquid strips the trailing '*' from the displayed term. */
  function searchPageUrl(q) {
    var wild = q.replace(/\*+$/, '') + '*';
    return '/search?q=' + encodeURIComponent(wild);
  }

  /* /search/suggest.json returns price as a decimal string in shop currency
     (e.g. "294.00"), NOT cents. Do not divide by 100. */
  function formatMoney(value) {
    if (typeof value === 'string') value = parseFloat(value);
    if (isNaN(value)) return '';
    return '$' + value.toFixed(2);
  }

  input.addEventListener('input', function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      fetchPredictive(input.value.trim());
    }, 300);
  });

  /* Submit form on Enter */
  form.addEventListener('submit', function (e) {
    var q = input.value.trim();
    e.preventDefault();
    if (!q) return;
    /* Navigate with the wildcard applied so the results page matches the
       dropdown (handles hyphen+digit SKU fragments like "D-0"). */
    window.location.href = searchPageUrl(q);
  });
})();
